
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle } from 'lucide-react';
import { LogoUpload } from '@/components/LogoUpload';

interface OnboardingFlowProps {
  onComplete: () => void;
}

const CURRENCIES = [
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'British Pound (£)' },
  { value: 'EGP', label: 'Egyptian Pound (E£)' },
  { value: 'SAR', label: 'Saudi Riyal (SAR)' },
  { value: 'AED', label: 'UAE Dirham (AED)' },
];

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    currency: 'USD',
    logoUrl: ''
  });

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('restaurants')
        .insert({
          owner_id: user.id,
          name: formData.name,
          phone: formData.phone || null,
          currency: formData.currency,
          logo_url: formData.logoUrl || null
        });

      if (error) throw error;

      toast({
        title: "Welcome aboard!",
        description: "Your restaurant has been set up successfully."
      });

      onComplete();
    } catch (error: any) {
      console.error('Error creating restaurant:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create restaurant. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const isStepComplete = (step: number) => {
    switch (step) {
      case 1: return formData.name.trim() !== '';
      case 2: return true; // Logo is optional
      case 3: return formData.phone.trim() !== '';
      case 4: return formData.currency !== '';
      case 5: return true;
      default: return false;
    }
  };

  const canProceed = isStepComplete(currentStep);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Welcome to Restaurant Manager</CardTitle>
          <CardDescription>Let's set up your restaurant in just a few steps</CardDescription>
          <div className="mt-4">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-gray-500 mt-2">Step {currentStep} of {totalSteps}</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold">What's your restaurant name?</h3>
                <p className="text-gray-600">This will be displayed on your menus and QR codes</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="restaurantName">Restaurant Name *</Label>
                <Input
                  id="restaurantName"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Mario's Pizza Palace"
                  className="text-lg"
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold">Upload your restaurant logo</h3>
                <p className="text-gray-600">This is optional, but helps customers recognize your brand</p>
              </div>
              <LogoUpload
                currentLogoUrl={formData.logoUrl}
                onLogoUpload={(url) => setFormData(prev => ({ ...prev, logoUrl: url }))}
                onLogoRemove={() => setFormData(prev => ({ ...prev, logoUrl: '' }))}
              />
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold">What's your restaurant phone number?</h3>
                <p className="text-gray-600">Customers can use this to contact you directly</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="e.g., +1 234 567 8900"
                  className="text-lg"
                />
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold">Choose your currency</h3>
                <p className="text-gray-600">This will be used for all pricing on your menus</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency *</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                  <SelectTrigger className="text-lg">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((currency) => (
                      <SelectItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold">Ready to launch!</h3>
                <p className="text-gray-600">Review your restaurant information below</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div>
                  <strong>Restaurant Name:</strong> {formData.name}
                </div>
                <div>
                  <strong>Phone:</strong> {formData.phone}
                </div>
                <div>
                  <strong>Currency:</strong> {CURRENCIES.find(c => c.value === formData.currency)?.label}
                </div>
                {formData.logoUrl && (
                  <div>
                    <strong>Logo:</strong> Uploaded ✓
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between pt-6">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              Back
            </Button>
            
            {currentStep < totalSteps ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleFinish}
                disabled={loading}
              >
                {loading ? 'Creating Restaurant...' : 'Finish Setup'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
